import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { notificationApi } from '../api/notificationApi';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

// Badge auf App-Icon aktualisieren
async function updateAppBadge(count) {
  if ('setAppBadge' in navigator) {
    try {
      if (count > 0) {
        await navigator.setAppBadge(count);
      } else {
        await navigator.clearAppBadge();
      }
    } catch (error) {
      console.log('App Badge nicht verfügbar:', error);
    }
  }
}

// Letzter bekannter Unread-Count für Title-Updates
let lastUnreadCount = 0;

// Browser-Tab-Titel mit Unread-Count aktualisieren
function updateDocumentTitle(count) {
  lastUnreadCount = count;

  // Aktuellen Titel holen und eventuell vorhandenen Count entfernen
  const currentTitle = document.title.replace(/^\(\d+\)\s*/, '');

  if (count > 0) {
    document.title = `(${count}) ${currentTitle}`;
  } else {
    document.title = currentTitle;
  }
}

// Titel aktualisieren wenn sich der Basis-Titel ändert (z.B. Firmenname geladen)
// Dies wird von außen aufgerufen wenn der Firmenname gesetzt wird
export function refreshTitleWithCount() {
  if (lastUnreadCount > 0) {
    const currentTitle = document.title.replace(/^\(\d+\)\s*/, '');
    document.title = `(${lastUnreadCount}) ${currentTitle}`;
  }
}

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) {
      setUnreadCount(0);
      updateAppBadge(0);
      updateDocumentTitle(0);
      return;
    }
    try {
      const count = await notificationApi.getUnreadCount();
      setUnreadCount(count || 0);
      updateAppBadge(count || 0);
      updateDocumentTitle(count || 0);
    } catch (error) {
      console.error('Fehler beim Laden der ungelesenen Benachrichtigungen:', error);
    }
  }, [user]);

  // Initial load + polling
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Sofort um 1 reduzieren (optimistisches Update)
  const decrementUnread = useCallback(() => {
    setUnreadCount(prev => {
      const newCount = Math.max(0, prev - 1);
      updateAppBadge(newCount);
      updateDocumentTitle(newCount);
      return newCount;
    });
  }, []);

  // Alle als gelesen markiert
  const clearUnread = useCallback(() => {
    setUnreadCount(0);
    updateAppBadge(0);
    updateDocumentTitle(0);
  }, []);

  // Manuell neu laden
  const refreshUnreadCount = useCallback(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  return (
    <NotificationContext.Provider value={{
      unreadCount,
      decrementUnread,
      clearUnread,
      refreshUnreadCount
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
